import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, EyeOff, MessageCircle, RotateCcw, Siren, Sparkles, Trash2 } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { PRAYER_ACCENT, excerpt, fill, prayerExcerptChars, timeAgo } from "@/lib/prayers";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PrayerReactions } from "./PrayerReactions";
import { Intercessors } from "./Intercessors";
import { PrayerEncouragements } from "./PrayerEncouragements";
import { PrayerToTestimony } from "./PrayerToTestimony";
import { PrayerStoryShare } from "./PrayerStoryShare";
import { PrayerLinkShare } from "./PrayerLinkShare";
import { ChainCounters, PrayMinuteButton, URGENT_COLOR } from "./PrayerChain";
import { ChainAuthorPanel } from "./PrayerChainAuthor";

// Lot 662 : avatar + nom de l'auteur → son profil (sauf demande anonyme).
const AuthorLink = ({ p, children, className }) => {
  const id = !p.author?.anonymous && p.author?.id;
  if (!id) return <div className={`${className} min-w-0`}>{children}</div>;
  return <Link to={`/profile/${id}`} className={`${className} min-w-0 hover:opacity-90`} data-testid={`prayer-author-link-${p.id}`} onClick={(e) => e.stopPropagation()}>{children}</Link>;
};

// Lot 673 — en-tête borné : nom tronqué (span `truncate` dans le flex, jamais le conteneur flex lui-même), badge
// « Urgente » / « Exaucée » insécable et non rétrécissable → il reste DANS la carte, même avec un nom très long
// (capture owner : badge coupé au bord droit du widget « Prions ensemble »).
export const PrayerHeader = ({ p, lang, t, compact = false }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <AuthorLink p={p} className="flex items-center gap-2.5 flex-1">
    {p.author?.anonymous ? (
      <span className={`${compact ? "h-8 w-8" : "h-9 w-9"} rounded-full grid place-items-center shrink-0`} style={{ background: "var(--yalola-surface-alt)" }}>
        <EyeOff size={16} style={{ color: "var(--yalola-text-muted)" }} />
      </span>
    ) : (
      <Avatar className={`${compact ? "h-8 w-8" : "h-9 w-9"} shrink-0`}><AvatarImage src={avatarSrc(p.author)} /><AvatarFallback>{p.author?.name?.[0]}</AvatarFallback></Avatar>
    )}
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold flex items-center gap-1 min-w-0" style={{ color: "var(--yalola-text)" }}>
        <span className="truncate" data-testid={`prayer-author-name-${p.id}`}>{p.author?.name}</span>
        {!p.author?.anonymous && <VerifiedBadge user={p.author} size={12} />}
      </div>
      <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(p.created_at, lang)}</div>
    </div>
    </AuthorLink>
    {p.is_urgent && !p.is_answered && (
      <span className="chip text-[10px] font-semibold shrink-0 whitespace-nowrap" style={{ background: URGENT_COLOR, color: "#fff", borderColor: "transparent" }} data-testid={`prayer-urgent-badge-${p.id}`}>
        <Siren size={10} className="inline mr-0.5" /> {t("prayers.urgent_badge", "Urgente")}
      </span>
    )}
    {p.is_answered && (
      <span className="chip text-[10px] font-semibold shrink-0 whitespace-nowrap" style={{ background: PRAYER_ACCENT, color: "#fff", borderColor: "transparent" }} data-testid={`prayer-answered-badge-${p.id}`}>
        <Sparkles size={10} className="inline mr-0.5" /> {t("prayers.answered_badge", "Exaucée")}
      </span>
    )}
  </div>
);

export const PrayerCard = ({ p, user, onPray, onReact, onDelete, onAnswer, onReopen, onPatch, highlight = false, openEncouragements = false, openChain = false, openChainPeople = false }) => {
  const { t, lang, config } = useApp();
  const [showEnc, setShowEnc] = useState(openEncouragements);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);
  const canManage = user && (p.is_mine || user.role === "super_admin");
  const limit = prayerExcerptChars(config);
  const long = (p.content || "").trim().length > limit;
  const shown = expanded || !long ? p.content : excerpt(p.content, limit);
  const onChainDone = (id, data) => onPatch?.(id, { chain: { minutes: data.minutes, people: data.people, praying_now: data.praying_now, mine: true }, prayed_by_me: true, pray_count: p.prayed_by_me ? p.pray_count : (p.pray_count || 0) + 1 });

  useEffect(() => {
    if (highlight && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlight]);

  return (
    <div ref={ref} className={`yalola-card p-4 flex flex-col gap-3 transition-shadow ${highlight ? "ring-2" : ""}`} style={highlight ? { "--tw-ring-color": PRAYER_ACCENT } : undefined} data-testid={`prayer-card-${p.id}`} data-highlight={highlight ? "1" : "0"}>
      <PrayerHeader p={p} lang={lang} t={t} />

      <p className="text-[15px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--yalola-text)" }} data-testid={`prayer-content-${p.id}`}>
        {shown}
        {long && (
          <button type="button" onClick={() => setExpanded((v) => !v)} className="ml-1 text-sm font-semibold hover:underline" style={{ color: PRAYER_ACCENT }} data-testid={`prayer-content-toggle-${p.id}`}>
            {expanded ? t("nav.see_less", "Voir moins") : t("nav.see_more", "Voir plus")}
          </button>
        )}
      </p>

      {p.is_answered && p.answered_note && (
        <div className="rounded-xl p-3 text-sm" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-text)" }} data-testid={`prayer-testimony-${p.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-wide mb-0.5" style={{ color: PRAYER_ACCENT }}>{t("prayers.testimony", "Témoignage")}</div>
          {p.answered_note}
        </div>
      )}

      {p.is_urgent && !p.is_answered && (
        <div className="rounded-xl p-3 space-y-2" style={{ background: URGENT_COLOR + "12", border: `1px solid ${URGENT_COLOR}44` }} data-testid={`prayer-chain-${p.id}`} data-mine={p.is_mine ? "1" : "0"}>
          {p.is_mine ? (
            // Lot 656 : l'auteur ne prie pas pour lui-même — il suit sa chaîne (qui a prié, merci)
            <ChainAuthorPanel p={p} onPatch={onPatch} openPeople={openChainPeople} />
          ) : (
            <>
              <ChainCounters chain={p.chain} testId={`prayer-chain-counters-${p.id}`} />
              <PrayMinuteButton p={p} onCompleted={onChainDone} autoOpen={openChain} />
            </>
          )}
        </div>
      )}

      <PrayerReactions p={p} user={user} onPray={onPray} onReact={onReact} />

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Intercessors p={p} user={user} />
        <button
          type="button"
          onClick={() => setShowEnc((v) => !v)}
          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
          style={{ color: showEnc ? PRAYER_ACCENT : "var(--yalola-text-muted)" }}
          aria-expanded={showEnc}
          data-testid={`prayer-encourage-toggle-${p.id}`}
        >
          <MessageCircle size={14} />
          {p.comment_count > 0 ? fill(t("prayers.encouragements_n", "{n} encouragement(s)"), { n: p.comment_count }) : t("prayers.encourage", "Encourager")}
        </button>
      </div>

      {showEnc && <PrayerEncouragements p={p} user={user} canManage={!!canManage} onCount={(n) => onPatch?.(p.id, { comment_count: n })} />}

      {user && (
        <div className="flex items-center gap-2 pt-1 flex-wrap border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`prayer-manage-${p.id}`}>
          <PrayerToTestimony p={p} onPublished={(id, postId) => onPatch?.(id, { testimony_post_id: postId })} />
          <PrayerStoryShare p={p} />
          <PrayerLinkShare p={p} />
          <span className="flex-1" />
          {canManage && !p.is_answered && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAnswer(p)} data-testid={`prayer-answer-${p.id}`}>
              <CheckCircle2 size={14} className="mr-1" /> {t("prayers.mark_answered", "Exaucée")}
            </Button>
          )}
          {canManage && p.is_answered && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onReopen(p)} data-testid={`prayer-reopen-${p.id}`}><RotateCcw size={13} /></Button>
          )}
          {canManage && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onDelete(p)} data-testid={`prayer-delete-${p.id}`}><Trash2 size={13} /></Button>
          )}
        </div>
      )}
    </div>
  );
};
